import * as React from 'react'
import { cn } from '@/lib/utils'

/** `size` sets density: `default` (38px, Plan View's field height) or `sm` (30px,
 * for dense rows beside small buttons). Named to shadow the rarely-used native
 * `size` (character-width) attribute. */
type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  size?: 'default' | 'sm'
}

/**
 * What every field shares, on Plan View's field recipe: a white surface on the
 * strong line (3:1, so a field can be found without its label), a darker line on
 * hover, and on focus a blue border with a soft halo. An invalid field takes the
 * danger line. Exported so the other controls cannot drift from it.
 */
export const FIELD_CLASSES =
  'flex w-full rounded-md border border-line-strong bg-surface text-sm text-ink transition-[border-color,box-shadow] duration-100 placeholder:text-ink-subtle hover:border-ink-subtle focus-visible:focus-field aria-[invalid=true]:border-danger-ink disabled:cursor-not-allowed disabled:opacity-50'

/**
 * A button that opens a picker (date, time) and reads as the field it fills: the
 * field's line, height and focus halo, which also shows while its popover is open.
 */
export const PICKER_CLASSES = `${FIELD_CLASSES} h-[38px] items-center gap-2 px-3 text-left tabular-nums data-[state=open]:focus-field`

/** A styled text input that forwards its ref and all native input props. */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, type, size = 'default', ...props },
  ref,
) {
  return (
    <input
      type={type}
      className={cn(
        FIELD_CLASSES,
        size === 'sm' ? 'h-[30px] px-2.5 text-control' : 'h-[38px] px-3',
        className,
      )}
      ref={ref}
      {...props}
    />
  )
})
